import { Button, Field, Spinner } from '@coreai/fluentui-react';
import { SearchableCombobox, type SearchableComboboxOption } from '@/components/shared/SearchableCombobox/SearchableCombobox';
import type { AzurePickerPage } from '@/types/azureResourcePicker';

export interface PickerQuery<T> {
  data: { pages: AzurePickerPage<T>[] } | undefined;
  error: Error | null;
  isFetching: boolean;
  isFetchNextPageError: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  refetch: () => Promise<unknown>;
}

export function pickerItems<T>(query: PickerQuery<T>): T[] {
  return query.data?.pages.flatMap((page) => page.items) ?? [];
}

export function retryPickerQuery<T>(query: PickerQuery<T>): void {
  void (query.isFetchNextPageError ? query.fetchNextPage() : query.refetch());
}

export function AzurePickerQueryControls<T>({ query, label }: { query: PickerQuery<T>; label: string }) {
  return <>
    {query.isFetching && <Spinner size="tiny" label={`Loading ${label}`} />}
    {query.hasNextPage && !query.error && !query.isFetching && <Button appearance="subtle"
      onClick={() => { void query.fetchNextPage(); }}>Load more {label}</Button>}
  </>;
}

interface AzurePickerSelectProps<T> {
  label: string;
  itemsLabel?: string | undefined;
  query: PickerQuery<T>;
  options: SearchableComboboxOption[];
  value: string | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder: string;
  fallbackDisplayValue?: string | undefined;
  noMatchMessage?: string | undefined;
}

export function AzurePickerSelect<T>({ label, itemsLabel, query, options, value, onChange, disabled = false, placeholder, fallbackDisplayValue, noMatchMessage }: AzurePickerSelectProps<T>) {
  return <div>
    <Field label={label}>
      <SearchableCombobox options={options} value={value} onChange={onChange} disabled={disabled || query.isFetching}
        aria-label={label} placeholder={placeholder} inlinePopup
        {...(fallbackDisplayValue !== undefined ? { fallbackDisplayValue } : {})}
        {...(noMatchMessage !== undefined ? { noMatchMessage } : {})} />
    </Field>
    <AzurePickerQueryControls query={query} label={itemsLabel ?? label.toLowerCase()} />
  </div>;
}